import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { SkillService } from '../../services/skill.service';
import { MenuItem, MessageService } from 'primeng/api';
import { PanelHubConfiguration } from '../../../shared-components/panel-hub/models/panel-hub-configuration';
import { PanelHubStateChangedEvent } from '../../../shared-components/panel-hub/models/panel-hub-state-changed-event';
import { PanelHubEventType } from '../../../shared-components/panel-hub/enums/panel-hub-eventtype';
import { PanelHubConstants } from '../../../shared-components/panel-hub/constants/panel-hub-constants';
import { Features } from '../../../shared-components/panel-hub/enums/features.enum';
import { PaginationConfigByFeature } from '../../../shared-components/panel-hub/constants/feature-pagination-configuration';
import { FilterConfig } from '../../../shared-components/panel-hub/models/filter-config';
import { AppliedFilters } from '../../../shared-components/panel-hub/models/applied-filters';
import { PaginationState } from '../../../shared-components/panel-hub/models/pagination-state';
import { SortCriteria } from '../../../shared-components/panel-hub/models/sort-criteria';
import { SortField } from '../../../shared-components/panel-hub/models/sort-field';
import { SortDirection } from '../../../shared-components/panel-hub/enums/sort-direction.enum';
import { DataType } from '../../../shared-components/panel-hub/enums/data-type.enum';
import { PanelEventHub } from '../../../shared-components/panel-hub/models/panel-event-hub';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

interface TrainingItemsQueryResult {
  totalRecords: number;
  data: any[];
  pageNumber: number;
  pageSize: number;
}

@Component({
  selector: 'app-skill-training-items-list',
  templateUrl: './skill-training-items-list.component.html',
  styleUrls: ['./skill-training-items-list.component.scss']
})
export class SkillTrainingItemsListComponent implements OnInit, OnDestroy {
  @Input() skillCode: string = '';
  @Input() isTrainingItemStatusUpdateEnabled: boolean = false;

  // Panel Hub Configuration
  panelHubConfig: PanelHubConfiguration = {
    feature: Features.SkillTrainingItems,
    standardFilterCount: PanelHubConstants.StandardFilterLength,
    showSelectAll: true,
    enableShowAllToggle: true,
    enableSelectAll: true,
    enableSelection: true,
    selectionIdField: 'trainingItemId',
    showAllLabel: 'Show all',
    showAllTooltip: 'By default, only Active Training Items are displayed. Check that check box to display also the Removed Training Items',
    enableToggling: false,
    enablePanelCardClick: false,
    enablePanelHighLight: false,
    enablePanelCardView: false,
    enableExport: false,
    enableExpandCollapse: false,
    enableViewModes: false,
    enableAssignmentView: false,
    enableWhereAmIUsedView: false,
    filterConfigs: [
      { displayName: 'Type', fieldName: 'trainingItemType', options: [
        { name: 'Document', value: '1' },
        { name: 'Course', value: '2' }
      ]},
      { displayName: 'Status', fieldName: 'status', options: [
        { name: 'Active', value: '1' },
        { name: 'Removed', value: '2' }
      ]}
    ],
    sortOptions: [
      { fieldDisplayName: 'Title', fieldName: 'title', type: DataType.String },
      { fieldDisplayName: 'Type', fieldName: 'trainingItemType', type: DataType.Number },
      { fieldDisplayName: 'Status', fieldName: 'status', type: DataType.Number }
    ] as SortField[],
    paginationConfig: PaginationConfigByFeature[Features.SkillTrainingItems]
  };

  // Data
  pagedData: TrainingItemsQueryResult = { totalRecords: 0, data: [], pageNumber: 1, pageSize: 0 };
  showLoader: boolean = false;
  noData: boolean = false;
  private allTrainingItems: any[] = []; // Cache for all training items from backend

  // State
  currentFilters: AppliedFilters | null = null;
  currentPagination: PaginationState | null = null;
  currentSort: SortCriteria | null = null;
  isShowAllSelected: boolean = false;
  panelEventHub!: PanelEventHub;
  private isDataCached: boolean = false; // Flag to track if data has been fetched

  // Selection (Client-side: simplified for cached data)
  selectedTrainingItemIds: Set<string> = new Set(); // Simple tracking of selected item IDs

  // Status update split button
  statusItems: MenuItem[] = [];

  constructor(
    private skillService: SkillService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initializePanel();
    this.initializeStatusMenu();
  }

  ngOnDestroy(): void {
    // Cleanup
  }

  /**
   * Initialize panel configuration
   */
  private initializePanel(): void {
    this.resetPanelConfiguration();
  }

  /**
   * Panel hub initialized event
   */
  onPanelEventHubInitialized(event: PanelEventHub): void {
    this.panelEventHub = event;
    this.loadInitialData();
  }

  /**
   * Panel hub state changed event
   */
  onPanelHubStateChanged = (event: PanelHubStateChangedEvent) => {
    switch (event.type) {
      case PanelHubEventType.FilterChanged:
        this.currentFilters = event.state.filters || null;
        if (this.currentPagination) this.currentPagination.pageNumber = 1;
        this.clearSelection(); // Clear selection when filters change (UX consistency)
        this.processAndDisplayTrainingItems(); // Client-side: no backend call
        break;
      case PanelHubEventType.SortChanged:
        this.currentSort = event.state.sort || null;
        if (this.currentPagination) this.currentPagination.pageNumber = 1;
        this.processAndDisplayTrainingItems(); // Client-side: no backend call
        break;
      case PanelHubEventType.PaginationChanged:
        this.currentPagination = event.state.pagination || null;
        this.processAndDisplayTrainingItems(); // Client-side: no backend call
        break;
      case PanelHubEventType.ShowAllChanged:
        this.isShowAllSelected = event.state.isShowAllChanged || false;
        this.currentFilters = null;
        if (this.currentPagination) this.currentPagination.pageNumber = 1;
        this.clearSelection(); // Clear selection when Show All toggle changes
        this.processAndDisplayTrainingItems(); // Client-side: no backend call
        break;
      case PanelHubEventType.ItemSelectionChanged:
        // Client-side pagination: convert panel-hub selection state to simple Set
        this.updateSelectedItems(event.state);
        break;
    }
  };

  /**
   * Update selected items (client-side simplified tracking)
   * Panel-hub uses selectedIds + deselectedIds + isSelectAllAcrossPages for server-side pagination
   * For client-side, we convert this to a simple Set of selected IDs since we have all data cached
   */
  private updateSelectedItems(state: any): void {
    if (state.isSelectAllAcrossPages) {
      // Select All mode: all items are selected except those in deselected list
      this.selectedTrainingItemIds = new Set(
        this.allTrainingItems
          .map(item => item.trainingItemId.toString())
          .filter(id => !state.deselectedItems?.includes(id))
      );
    } else {
      // Individual selection: only selected items
      this.selectedTrainingItemIds = new Set(
        (state.selectedItems || []).map((id: any) => id.toString())
      );
    }
  }

  /**
   * Clear selection
   */
  private clearSelection(): void {
    this.selectedTrainingItemIds.clear();
    // Also clear panel-hub's internal selection state so the checkboxes
    // in the UI are visually deselected.
    this.panelEventHub?.clearSelection();
  }

  /**
   * Get selected training items (full objects, not just IDs)
   */
  getSelectedTrainingItems(): any[] {
    return this.allTrainingItems.filter(item => 
      this.selectedTrainingItemIds.has(item.trainingItemId.toString())
    );
  }

  /**
   * Get count of selected items
   */
  getSelectedCount(): number {
    return this.selectedTrainingItemIds.size;
  }

  /**
   * Reset panel configuration
   */
  private resetPanelConfiguration = () => {
    this.currentPagination = {
      pageNumber: this.panelHubConfig.paginationConfig!.startPage,
      pageSize: this.panelHubConfig.paginationConfig!.defaultPageSize,
      totalRecords: 0
    };
    this.currentFilters = null;
    this.currentSort = null;
    this.selectedTrainingItemIds.clear(); // Clear selection on reset
  };

  /**
   * Load initial data
   */
  private loadInitialData(): void {
    if (!this.skillCode) {
      this.noData = true;
      return;
    }
    this.refreshPanelHubData();
  }

  /**
   * Refresh panel hub data
   */
  private refreshPanelHubData(): void {
    if (!this.currentPagination) {
      this.currentPagination = {
        pageNumber: this.panelHubConfig.paginationConfig!.startPage,
        pageSize: this.panelHubConfig.paginationConfig!.defaultPageSize,
        totalRecords: 0
      };
    }
    this.fetchPagedTrainingItems();
  }

  /**
   * Fetch paged training items from backend (only on first load)
   * If data is already cached, process from cache without backend call
   */
  private fetchPagedTrainingItems(): void {
    if (!this.currentPagination || !this.skillCode) return;

    // If data is already cached, process from cache (client-side only)
    if (this.isDataCached) {
      this.processAndDisplayTrainingItems();
      return;
    }

    // First load: fetch from backend
    this.panelEventHub.startLoader();
    this.showLoader = true;

    this.skillService.getLinkedTrainingItems(this.skillCode)
      .pipe(
        catchError(err => {
          this.pagedData = { totalRecords: 0, data: [], pageNumber: 1, pageSize: 0 };
          this.noData = true;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error loading training items',
            sticky: true
          });
          return of(null);
        }),
        finalize(() => {
          this.panelEventHub.stopLoader();
          this.showLoader = false;
        })
      )
      .subscribe(result => {
        if (!result) return;
        // Cache the data and process it
        this.allTrainingItems = result;
        this.isDataCached = true;
        this.processAndDisplayTrainingItems();
      });
  }

  /**
   * Process cached training items with filters and pagination (client-side only, no backend call)
   */
  private processAndDisplayTrainingItems(): void {
    if (this.allTrainingItems.length === 0) {
      this.pagedData = { totalRecords: 0, data: [], pageNumber: 1, pageSize: 0 };
      this.noData = true;
      return;
    }

    // Map to display format
    let processedItems = this.allTrainingItems.map(item => ({
      ...item,
      typeName: item.trainingItemType === 1 ? 'Document' : item.trainingItemType === 2 ? 'Course' : 'Unknown',
      statusName: item.status === 1 ? 'Active' : item.status === 2 ? 'Removed' : 'Unknown'
    }));

    // Step 1: Apply "Show all" toggle as master control for visibility
    // If Show all is OFF: only show Active items (status = 1)
    // If Show all is ON: proceed with all items (no status restriction at this step)
    if (!this.isShowAllSelected) {
      // Show all is OFF: Check for conflicting Status filter
      // If Status filter is set to "Removed" while Show all is OFF, it's a conflict - show no data
      if (this.currentFilters && (this.currentFilters as any).filters?.length > 0) {
        const filtersArray = (this.currentFilters as any).filters;
        const statusFilterObj = filtersArray.find((f: any) => f.field === 'status');
        
        // Check if Status filter is explicitly set to only "Removed" (status = 2)
        if (statusFilterObj && statusFilterObj.values?.length > 0) {
          const statusValues = statusFilterObj.values.map((s: any) => parseInt(s));
          // If filter only contains "Removed" (2) and not "Active" (1), it's a conflict
          if (statusValues.includes(2) && !statusValues.includes(1)) {
            // Conflict: Show all is OFF but user is trying to show Removed items
            processedItems = []; // Show no data
          } else {
            // Filter contains "Active", so proceed normally
            processedItems = processedItems.filter(item => item.status === 1);
          }
        } else {
          // No Status filter, just show Active
          processedItems = processedItems.filter(item => item.status === 1);
        }
      } else {
        // No filters at all, just show Active
        processedItems = processedItems.filter(item => item.status === 1);
      }
    }

    // Step 2: Apply Type filter (independent of Show all state)
    if (this.currentFilters && (this.currentFilters as any).filters?.length > 0) {
      const filtersArray = (this.currentFilters as any).filters;
      
      // Find Type filter in array
      const typeFilterObj = filtersArray.find((f: any) => f.field === 'trainingItemType');
      if (typeFilterObj && typeFilterObj.values?.length > 0) {
        const typeFilter = typeFilterObj.values.map((t: any) => parseInt(t));
        processedItems = processedItems.filter(item => typeFilter.includes(item.trainingItemType));
      }
    }

    // Step 3: Apply Status filter (only when Show all is ON)
    // Status filter allows refinement only after Show all is enabled
    if (this.isShowAllSelected && this.currentFilters && (this.currentFilters as any).filters?.length > 0) {
      const filtersArray = (this.currentFilters as any).filters;
      
      // Find Status filter in array
      const statusFilterObj = filtersArray.find((f: any) => f.field === 'status');
      if (statusFilterObj && statusFilterObj.values?.length > 0) {
        const statusFilter = statusFilterObj.values.map((s: any) => parseInt(s));
        processedItems = processedItems.filter(item => statusFilter.includes(item.status));
      }
    }

    // Step 4: Apply text search filter (client-side, case-insensitive)
    // Matches against trainingItemId, title, and trainingItemGroups
    // Type & Status are excluded — they have dedicated dropdown filters
    const searchText = (this.currentFilters as any)?.searchText?.trim().toLowerCase();
    if (searchText && searchText.length > 0) {
      processedItems = processedItems.filter(item => {
        const idMatch = item.trainingItemId?.toString().toLowerCase().includes(searchText);
        const titleMatch = item.title?.toLowerCase().includes(searchText);
        const groupMatch = Array.isArray(item.trainingItemGroups) &&
          item.trainingItemGroups.some((g: string) => g?.toLowerCase().includes(searchText));
        return idMatch || titleMatch || groupMatch;
      });
    }

    // Step 5: Apply sort (client-side)
    if (this.currentSort && this.currentSort.field) {
      processedItems = this.applySort(processedItems, this.currentSort);
    }

    // Calculate pagination
    const startIndex = ((this.currentPagination!.pageNumber || 1) - 1) * (this.currentPagination!.pageSize || 25);
    const endIndex = startIndex + (this.currentPagination!.pageSize || 25);
    const paginatedItems = processedItems.slice(startIndex, endIndex);

    // Update the display
    this.pagedData = {
      totalRecords: processedItems.length,
      data: paginatedItems,
      pageNumber: this.currentPagination!.pageNumber || 1,
      pageSize: this.currentPagination!.pageSize || 25
    };
    this.noData = this.pagedData.data.length === 0;
  }



  /**
   * Open document details
   */
  openDocument(trainingItemId: string, version: string): void {
    const url = location.origin.concat("/eqms.documentDetails/", trainingItemId, ":", version);
    window.open(url, "_blank");
  }

  /**
   * Open course details
   */
  openCourse(trainingItemId: string): void {
    const url = location.origin.concat("/training/eqms.courses/", trainingItemId);
    window.open(url, "_blank");
  }

  /**
   * Client-side sort by field & direction. Handles string, number, and date.
   */
  private applySort(items: any[], sort: SortCriteria): any[] {
    const field = sort.field;
    const direction = sort.sortDirection === SortDirection.Descending ? -1 : 1;
    const type = sort.type ?? DataType.String;

    return [...items].sort((a, b) => {
      const aVal = a?.[field];
      const bVal = b?.[field];

      // Null / undefined always sort last
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      let cmp = 0;
      switch (type) {
        case DataType.Number:
          cmp = Number(aVal) - Number(bVal);
          break;
        case DataType.Date:
          cmp = new Date(aVal).getTime() - new Date(bVal).getTime();
          break;
        default:
          cmp = aVal.toString().localeCompare(bVal.toString(), undefined, { sensitivity: 'base', numeric: true });
      }
      return cmp * direction;
    });
  }

  /**
   * Initialize the status update menu (Active / Removed)
   */
  private initializeStatusMenu(): void {
    this.statusItems = [
      {
        label: 'Active',
        command: () => this.updateStatusForSelected(1)
      },
      {
        label: 'Removed',
        command: () => this.updateStatusForSelected(2)
      }
    ];
  }

  /**
   * Returns true when no items are selected, or when the current user is not
   * authorized to update training item statuses (used to disable split button).
   */
  get isStatusButtonDisabled(): boolean {
    return this.selectedTrainingItemIds.size === 0 || !this.isTrainingItemStatusUpdateEnabled;
  }

  /**
   * Update status for all selected training items.
   * UI validation: items already in the requested status are skipped and
   * surfaced via a warning toaster. Remaining items are updated via API
   * and a success toaster is shown.
   */
  private updateStatusForSelected(newStatus: number): void {
    const selectedItems = this.getSelectedTrainingItems();
    if (selectedItems.length === 0) return;

    const statusName = newStatus === 1 ? 'Active' : 'Removed';

    // Split into already-in-status (skipped) vs needs-update (valid)
    const skippedItems = selectedItems.filter(item => item.status === newStatus);
    const validItems = selectedItems.filter(item => item.status !== newStatus);

    // Show warning for items already in the requested status
    if (skippedItems.length > 0) {
      const itemsList = skippedItems
        .map(item => `${item.trainingItemId} ${item.title ?? ''}`.trim())
        .join(', ');
      this.messageService.add({
        severity: 'warn',
        summary: `The following Training Items have not been updated because they are already in the selected Status:\n${itemsList}`,
        life: 10000
      });
    }

    // If no items remain to update, clear selection and exit
    if (validItems.length === 0) {
      this.clearSelection();
      return;
    }

    // Proceed with one batch API call for valid items
    this.panelEventHub.startLoader();
    this.showLoader = true;

    const payload = {
      trainingItemIds: validItems.map(item => item.trainingItemId),
      status: newStatus
    };

    // Clear selection immediately on API dispatch so the user can't re-trigger
    // the action against the same items while the request is in flight.
    this.clearSelection();

    this.skillService.updateTrainingItemsStatus(this.skillCode, payload)
      .pipe(
        catchError(() => of(null)),
        finalize(() => {
          this.panelEventHub.stopLoader();
          this.showLoader = false;
        })
      )
      .subscribe(result => {
        if (result === null) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to update status for ${validItems.length} training item(s).`,
            sticky: true
          });
          return;
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Status updated to ${statusName} for ${validItems.length} training item(s).`
        });
        setTimeout(() => {
          this.isDataCached = false;
          this.fetchPagedTrainingItems();
        }, 5000);
      });
  }
}

